import React, { useState, useEffect } from "react";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import { useLocation } from "react-router-dom"; // Para obtener la ruta actual
import { format } from "date-fns";
import { useShoppingContext } from "../../../../context/ShoppingContext";
import "./AddControlAlimentosForm.css"; // Importa el archivo CSS

const AddControlAlimentoForm = () => {
  const [step, setStep] = useState(1);
  const {
    setLoading,
    aves,
    controlAves,
    alimentos,
    setShowAddControlAlimentos,
    granjaActiva,
    fetchControlAlimentos,
    selectedAve, // selectedAve si ya está preseleccionado
    selectedAlimento,
    renderDateTitle,
  } = useShoppingContext();

  const location = useLocation(); // Para monitorear la ruta actual
  const [formData, setFormData] = useState({
    ave_id: location.pathname === "/mis-granjas" ? "" : selectedAve?.id || "",
    fecha: format(new Date(), "yyyy-MM-dd"), // Utilizar el formato YYYY-MM-DD
    id_alimento: selectedAlimento?.id || "", // Preseleccionar el alimento
    ingesta_diaria: 0,
    sobra: 0,
    granjaId: granjaActiva,
  });

  useEffect(() => {
    if (formData.ave_id) {
      const selectedAve = aves.find((ave) => ave.id === formData.ave_id);
      if (selectedAve) {
        setFormData((prevData) => ({
          ...prevData,
          granjaId: selectedAve.granjaId,
          id_alimento: selectedAve.id_alimento || "", // Alimento del lote
          ingesta_diaria: selectedAve.ingesta_diaria || 0, // Asignar la ingesta automáticamente
        }));
      }
    }
  }, [formData.ave_id, aves]);

  const selectedAveName =
    aves.find((ave) => ave.id === parseInt(formData.ave_id))?.nombre ||
    "Lote no seleccionado";

  useEffect(() => {
    if (formData.ave_id) {
      const selectedAve = aves.find((ave) => ave.id === formData.ave_id);
      if (selectedAve) {
        setFormData((prevData) => ({
          ...prevData,
          granjaId: selectedAve.granjaId,
          // Solo actualiza id_alimento e ingesta_diaria si aún no están seleccionados
          id_alimento: prevData.id_alimento || selectedAve.id_alimento || "",
          ingesta_diaria:
            prevData.ingesta_diaria || selectedAve.ingesta_diaria || 0,
        }));
      }
    }
  }, [formData.ave_id, aves]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "ave_id") {
      const selectedAve = aves.find((ave) => ave.id === parseInt(value));
      if (selectedAve) {
        setFormData((prevData) => ({
          ...prevData,
          id_alimento: selectedAve.id_alimento || "",
          ingesta_diaria: selectedAve.ingesta_diaria || 0,
        }));
      }
    }
  };

  const handlePreviousStep = () => {
    if (step === 1) {
      setShowAddControlAlimentos(false); // Cerrar el formulario
    } else {
      setStep(step - 1);
    }
  };

  const handleNextStep = () => {
    if (!formData.ave_id) {
      alert("Por favor, seleccione un lote antes de continuar.");
    } else if (!formData.id_alimento) {
      alert("Por favor, seleccione un alimento antes de continuar.");
    } else {
      setStep(step + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form data at submit:", formData);

    const existingRecord = controlAves.find(
      (registro) =>
        registro.fecha === formData.fecha &&
        registro.ave_id === parseInt(formData.ave_id)
    );

    if (existingRecord) {
      console.error("Ya existe un registro para esta fecha y lote.");
      return;
    }

    const updatedFormData = {
      ...formData,
      ave_id: parseInt(formData.ave_id),
      id_alimento: formData.id_alimento ? parseInt(formData.id_alimento) : null,
      ingesta_diaria: parseFloat(formData.ingesta_diaria),
      sobra: parseFloat(formData.sobra),
    };

    try {
      setShowAddControlAlimentos(false);
      setLoading("Creando control de alimentos");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/control-alimentos`,
        updatedFormData
      );
      console.log(
        "Registro de control de alimentos agregado con éxito:",
        response.data
      );
      fetchControlAlimentos();
      setLoading(false);
    } catch (error) {
      console.error("Error al procesar el formulario:", error);
    }
  };

  const resetFormData = () => {
    setFormData({
      ave_id: location.pathname === "/mis-granjas" ? "" : selectedAve?.id || "",
      fecha: format(new Date(), "yyyy-MM-dd"),
      id_alimento: selectedAlimento?.id || "", // Resetear con el alimento seleccionado
      ingesta_diaria: 0,
      sobra: 0,
      granjaId: granjaActiva,
    });
  };

  useEffect(() => {
    if (location.pathname === "/mis-granjas") {
      resetFormData();
      setStep(1);
    }
  }, [location.pathname]);

  return (
    <div className="add-form-container">
      <form className="add-control-alimentos-form" onSubmit={handleSubmit}>
        {step === 1 && (
          <>
            <div className="form-group">
              <label htmlFor="ave_id" className="form-label">
                Seleccionar Ave
              </label>
              <select
                id="ave_id"
                name="ave_id"
                value={formData.ave_id}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Seleccione un lote</option>
                {aves.map((ave) => (
                  <option key={ave.id} value={ave.id}>
                    {ave.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="id_alimento" className="form-label">
                Tipo de Alimento
              </label>
              <select
                id="id_alimento"
                name="id_alimento"
                value={formData.id_alimento}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Seleccione un alimento</option>
                {alimentos.map((alimento) => (
                  <option key={alimento.id} value={alimento.id}>
                    {alimento.nombre + " | (" + alimento.cantidad + ") kg"}
                  </option>
                ))}
              </select>
            </div>

            <div className="add-ave-form-buttons">
              <button
                type="button"
                className="btn btn-green-outline"
                onClick={() => setShowAddControlAlimentos(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-green"
                onClick={handleNextStep}
              >
                Siguiente
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2>{selectedAveName}</h2>

            <div className="form-group">
              <label htmlFor="ingesta_diaria" className="form-label">
                Alimento consumido (kg)
              </label>
              <input
                type="number"
                id="ingesta_diaria"
                name="ingesta_diaria"
                value={formData.ingesta_diaria}
                onChange={handleChange}
                onFocus={(e) => e.target.value === "0" && (e.target.value = "")}
                onBlur={(e) => e.target.value === "" && (e.target.value = "0")}
                className="form-input"
                required
                inputMode="numeric"
              />
            </div>
            <div className="form-group">
              <label htmlFor="sobra" className="form-label">
                Sobra (kg)
              </label>
              <input
                type="number"
                id="sobra"
                name="sobra"
                value={formData.sobra}
                onChange={handleChange}
                onFocus={(e) => e.target.value === "0" && (e.target.value = "")}
                onBlur={(e) => e.target.value === "" && (e.target.value = "0")}
                className="form-input"
                inputMode="numeric"
              />
            </div>

            <div className="form-group mt-3">
              <input
                type="date"
                id="fecha"
                name="fecha"
                value={formData.fecha}
                onChange={(e) => handleChange(e)}
                className="add-ave-form-input"
                max={format(new Date(), "yyyy-MM-dd")} // Max es la fecha actual
                required
              />
            </div>
            <p>Fecha seleccionada: {renderDateTitle(formData.fecha)}</p>

            <div className="add-ave-form-buttons">
              <button
                type="button"
                className="btn btn-green-outline"
                onClick={() => handlePreviousStep()}
              >
                Cambiar Ave
              </button>
              <button type="submit" className="btn btn-green">
                Registrar
              </button>
            </div>
          </>
        )}
        <Tooltip />
      </form>
    </div>
  );
};

export default AddControlAlimentoForm;
